import { Stack, styled, Typography } from "@mui/material";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { ListMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/ListMagnifyingGlass";
import { Dispatch, useEffect, useState } from "react";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import { NewSearchTextBox } from "../../features/data-under-management/components/NewSearchTextBox";
import { CheckboxFilters } from "../../features/keys/components/CheckboxFilters";
import { useDebounce } from "../../lib/Hooks";
import { toTitleCase } from "../../lib/Util";
import { SourceNode, SourceLeaf } from "../common/FilterPublishedArtifacts";
import { IEventLogFilters } from "../EventLog/Filters/types";
import { FilterPanel, Item } from "../Filters/Common";
import { DateRangeFilters } from "../Filters/DateRangeFilters";
import { ExportButton } from "../Filters/ExportButton";
import { ProjectsFilter } from "../Filters/ProjectsFilter";
import { CheckboxWithLabel } from "../ui/CheckboxWithLabel";
import { initialVisibilityModelEventsDatagrid } from "./EventsDataGrid";

const StyledEye = styled(EyeIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));

type SelectableItems = "reportDate" | "occurred" | "severity" | "name";

const CHECKBOX_OPTIONS = [
  { value: DataGridColumns.ReportDateTime, label: "Report date" },
  { value: DataGridColumns.Created, label: "Occurred" },
  { value: DataGridColumns.Severity, label: "Severity" },
  { value: DataGridColumns.ProjectName, label: "Project name" },
  { value: DataGridColumns.DownloadUpload, label: "Download/Upload" },
  { value: DataGridColumns.Message, label: "Message" },
];

const COLUMNS = [
  DataGridColumns.ReportDateTime,
  DataGridColumns.Created,
  DataGridColumns.Severity,
  DataGridColumns.ProjectName,
  DataGridColumns.DownloadUpload,
  DataGridColumns.Message,
];

const SeverityFilters = ({ selected, onClick }: { selected?: string[]; onClick: (severity: string) => void }) => {
  return (
    <Stack sx={{ gap: "12px" }}>
      {["errors", "warnings", "info"].map((severity) => (
        <CheckboxWithLabel
          key={severity}
          value={severity}
          color="primary"
          checked={selected?.includes(severity) || false}
          onClick={() => onClick(severity)}
          label={toTitleCase(severity)}
          checkboxSx={{ marginRight: "8px" }}
        />
      ))}
    </Stack>
  );
};

const FilterItems = ({
  selectedItem,
  setSelectedItem,
  filter,
}: {
  selectedItem: SelectableItems;
  setSelectedItem: Dispatch<React.SetStateAction<SelectableItems>>;
  filter: IEventLogFilters;
}) => {
  return (
    <>
      <Item
        text="Report date"
        isSelected={selectedItem === "reportDate"}
        onClick={() => setSelectedItem("reportDate")}
        hasFiltersApplied={!!(filter.reportRange?.start && filter.reportRange.end)}
      />
      <Item
        text="Occurred"
        isSelected={selectedItem === "occurred"}
        onClick={() => setSelectedItem("occurred")}
        hasFiltersApplied={!!(filter.dateRange?.start && filter.dateRange.end)}
      />
      <Item
        text="Severity"
        isSelected={selectedItem === "severity"}
        onClick={() => setSelectedItem("severity")}
        hasFiltersApplied={!!filter.severity?.length}
      />
      <Item
        text="Project name"
        isSelected={selectedItem === "name"}
        onClick={() => setSelectedItem("name")}
        hasFiltersApplied={!!filter.uploadName?.length || !!filter.downloadName?.length || !!filter.projects?.length}
      />
    </>
  );
};

type RightPanelProps = {
  filter: IEventLogFilters;
  setFilter: Dispatch<React.SetStateAction<IEventLogFilters>>;
  selectedItem: SelectableItems;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
};

const RightPanel = ({
  filter,
  setFilter,
  selectedItem,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
}: RightPanelProps) => {
  const handleSelectCallback = (nodes: SourceLeaf[], takeOut?: boolean) => {
    setFilter((prevFilter) => {
      const newFilterValue = {
        downloadNames: new Set(prevFilter.downloadName),
        uploadNames: new Set(prevFilter.uploadName),
        projects: new Set(prevFilter.projects),
      };

      nodes.forEach((node) => {
        const nodeFilterValue = node.filterValue;
        const isDownload = "importName" in nodeFilterValue;
        const isUpload = "exportName" in nodeFilterValue;
        if (!nodeFilterValue) {
          return;
        }
        if (takeOut) {
          if (isDownload && nodeFilterValue.importName) {
            newFilterValue.downloadNames.delete(nodeFilterValue.importName);
          } else if (isUpload && nodeFilterValue.exportName) {
            newFilterValue.uploadNames.delete(nodeFilterValue.exportName);
          }
          if (nodeFilterValue.project) {
            newFilterValue.projects.delete(nodeFilterValue.project);
          }
        } else {
          if (isDownload && nodeFilterValue.importName) {
            newFilterValue.downloadNames.add(nodeFilterValue.importName);
          } else if (isUpload && nodeFilterValue.exportName) {
            newFilterValue.uploadNames.add(nodeFilterValue.exportName);
          }
          if (nodeFilterValue.project) {
            newFilterValue.projects.add(nodeFilterValue.project);
          }
        }
      });

      return {
        ...prevFilter,
        downloadName: Array.from(newFilterValue.downloadNames),
        uploadName: Array.from(newFilterValue.uploadNames),
        projects: Array.from(newFilterValue.projects),
      };
    });
  };
  if (selectedItem === "reportDate") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, reportRange: { start, end } })}
        start={filter.reportRange?.start || null}
        end={filter.reportRange?.end || null}
        onClearThisFilter={() => setFilter({ ...filter, reportRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "occurred") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, dateRange: { start, end } })}
        start={filter.dateRange?.start || null}
        end={filter.dateRange?.end || null}
        onClearThisFilter={() => setFilter({ ...filter, dateRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "severity") {
    return (
      <SeverityFilters
        selected={filter.severity}
        onClick={(severity) => {
          if (filter.severity?.includes(severity)) {
            setFilter({ ...filter, severity: filter.severity.filter((s) => s !== severity) });
          } else {
            setFilter({ ...filter, severity: [...(filter.severity || []), severity] });
          }
        }}
      />
    );
  }
  if (selectedItem === "name") {
    return (
      <ProjectsFilter
        onClearThisFilter={() => setFilter({ ...filter, projects: [], downloadName: [], uploadName: [] })}
        selectedNodesInFilter={selectedNodesInFilter}
        nonSelectedNodesInFilter={nonSelectedNodesInFilter}
        onSelectCallback={handleSelectCallback}
      />
    );
  }

  return null;
};

const handleClearAllFilters = (setFilter: Dispatch<React.SetStateAction<IEventLogFilters>>) => {
  setFilter({
    search: "",
    reportRange: { start: null, end: null },
    dateRange: { start: null, end: null },
    severity: [],
  });
};

type EventFiltersProps = {
  filter: IEventLogFilters;
  setFilter: Dispatch<React.SetStateAction<IEventLogFilters>>;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
  loading: boolean;
  queryVariables: IEventLogFilters;
};

const isFilteringEvents = (filter: IEventLogFilters) =>
  Boolean(filter.reportRange?.start) ||
  Boolean(filter.reportRange?.end) ||
  Boolean(filter.dateRange?.start) ||
  Boolean(filter.dateRange?.end) ||
  Boolean(filter.severity?.length) ||
  Boolean(filter.search) ||
  Boolean(filter.projects?.length) ||
  Boolean(filter.downloadName?.length) ||
  Boolean(filter.uploadName?.length);

const EventFilters = ({
  filter,
  setFilter,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
  loading,
  queryVariables,
}: EventFiltersProps) => {
  const { user, updateDataGridPreferences } = useAuth();
  const [searchValue, setSearchValue] = useState(filter.search || "");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedItem, setSelectedItem] = useState<SelectableItems>("reportDate");

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      search: debouncedSearchValue,
    }));
  }, [debouncedSearchValue, setFilter]);

  const getSelectedOptions = () => {
    const columnVisibilityModel =
      user.datagridPreferences.events?.columnVisibilityModel || initialVisibilityModelEventsDatagrid;
    return COLUMNS.filter((col) => columnVisibilityModel?.[col]);
  };

  const columnsWidth = user.datagridPreferences.events?.columnsWidth;
  const showClearAllFilters = isFilteringEvents(filter);

  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: "28px" }}>
      <Stack direction="row" sx={{ gap: "16px", alignItems: "center" }}>
        <ListMagnifyingGlassIcon size={32} />
        <Typography variant="heading.three">Events</Typography>
      </Stack>
      <Stack direction="row" sx={{ gap: "12px", alignItems: "center", height: "48px" }}>
        <Stack sx={{ width: "300px" }}>
          <NewSearchTextBox value={searchValue} onChange={setSearchValue} placeholder="Search" />
        </Stack>
        <FilterPanel
          loading={loading}
          onClearAllFilters={() => handleClearAllFilters(setFilter)}
          showClearAllFilters={showClearAllFilters}
          filterCount={showClearAllFilters ? 1 : 0}
          rightPanel={
            <RightPanel
              filter={filter}
              setFilter={setFilter}
              selectedItem={selectedItem}
              selectedNodesInFilter={selectedNodesInFilter}
              nonSelectedNodesInFilter={nonSelectedNodesInFilter}
            />
          }
          biggerWidth={selectedItem === "name"}
          filterItems={<FilterItems selectedItem={selectedItem} setSelectedItem={setSelectedItem} filter={filter} />}
        />
        <CheckboxFilters
          Icon={<StyledEye size={24} />}
          options={CHECKBOX_OPTIONS}
          onClickOption={(value) => {
            const visibilityModel =
              user.datagridPreferences.events?.columnVisibilityModel || initialVisibilityModelEventsDatagrid;
            updateDataGridPreferences("events", {
              columnsOrder: user.datagridPreferences.events?.columnsOrder,
              columnsWidth: columnsWidth,
              columnVisibilityModel: {
                ...visibilityModel,
                [value]: !visibilityModel[value],
              },
            });
          }}
          selectedOptions={getSelectedOptions()}
        />
        {<ExportButton queryVariables={queryVariables} query="events" />}
      </Stack>
    </Stack>
  );
};

export { EventFilters, isFilteringEvents };
